import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Card,
  CardContent,
  Typography,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination,
} from "@mui/material";
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  RestartAlt as RestartAltIcon,
  RestorePage as RestoreIcon,
  InfoOutlined as InfoOutlinedIcon,
} from "@mui/icons-material";
import { adminApiService } from "../../../utils/api";
import { useTranslation } from "react-i18next";
import { messages } from "../../../locales/messages";
import { toastError, toastSuccess } from "../../../utils/pageUtil/toast";
import { AccountCardWrapper } from "../../components/AccountCardWrapper";
import {
  ConfigurationListResponseDto,
  ConfigurationResponseDto,
  UpdateConfigurationDto,
  ConfigurationMetadataDto,
} from "@swagger/typescript-fetch-goflyer";
import { useAuthHeaders } from "../../../hooks/useAuthHeaders";
import { BROWSE_CATEGORY_MERGE_GROUPS_KEY } from "../../../utils/browseCategoryMergeGroups";
import { BrowseCategoryMergeGroupsModal } from "./BrowseCategoryMergeGroupsModal";

interface ConfigMetadata {
  label: string;
  description: string;
  defaultValue: string;
  type: "string" | "number" | "boolean" | "json" | "select";
  options?: string[];
  category: string;
  requiresRestart: boolean;
  tooltip: string;
}

interface Configuration {
  key: string;
  value: string;
  metadata: ConfigMetadata;
  updatedAt: string;
}

const formatConfigValue = (
  rawValue: unknown,
  type: "string" | "number" | "boolean" | "json" | "select",
): string => {
  if (rawValue === null || rawValue === undefined) {
    return "";
  }
  if (type === "json" || (typeof rawValue === "object" && rawValue !== null)) {
    try {
      return JSON.stringify(rawValue, null, 2);
    } catch {
      return "";
    }
  }
  return String(rawValue);
};

/**
 * JSON values are edited from a dedicated editor rather than a raw text field.
 * Keys listed here have one on this page; everything else points the admin at
 * the matching admin page.
 */
const hasDedicatedEditor = (config: Configuration) =>
  config.key === BROWSE_CATEGORY_MERGE_GROUPS_KEY;

const isReadOnlyJson = (config: Configuration) =>
  config.metadata.type === "json" && !hasDedicatedEditor(config);

export const DataBaseDrivenVariablesPage = () => {
  const { t } = useTranslation();
  const authHeaders = useAuthHeaders();
  const [configurations, setConfigurations] = useState<Configuration[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [limit] = useState(10);
  const [restartWarningOpen, setRestartWarningOpen] = useState(false);
  const [pendingUpdate, setPendingUpdate] = useState<{
    key: string;
    value: any;
  } | null>(null);
  const [mergeGroupsConfig, setMergeGroupsConfig] =
    useState<Configuration | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchConfigurations = useCallback(async () => {
    setLoading(true);
    try {
      const response: ConfigurationListResponseDto =
        await adminApiService.gfMiscellaneousAdminControllerGetAllConfigurations(
          page,
          limit,
          selectedCategory === "all" ? undefined : selectedCategory,
          debouncedSearchTerm || undefined,
          authHeaders,
        );

      const configData: Configuration[] = response.configurations.map(
        (item: ConfigurationResponseDto) => {
          const metadataType = item.metadata?.type;
          let configType: "string" | "number" | "boolean" | "json" | "select" =
            "string";

          if (metadataType === ConfigurationMetadataDto.TypeEnum.Number) {
            configType = "number";
          } else if (
            metadataType === ConfigurationMetadataDto.TypeEnum.Boolean
          ) {
            configType = "boolean";
          } else if (
            metadataType === ConfigurationMetadataDto.TypeEnum.Select
          ) {
            configType = "select";
          } else if (
            metadataType === ConfigurationMetadataDto.TypeEnum.Json ||
            (typeof item.value === "object" && item.value !== null)
          ) {
            configType = "json";
          }

          const rawDefault =
            item.metadata?.defaultValue !== undefined &&
            item.metadata?.defaultValue !== null
              ? item.metadata.defaultValue
              : item.value;

          return {
            key: item.key,
            value: formatConfigValue(item.value, configType),
            metadata: {
              label: item.metadata?.label || item.key,
              description: item.metadata?.description || "",
              defaultValue: formatConfigValue(rawDefault, configType),
              type: configType,
              options: item.metadata?.options || [],
              category: item.metadata?.category || "Other",
              requiresRestart: item.metadata?.requiresRestart || false,
              tooltip: item.metadata?.tooltip || "",
            },
            updatedAt: item.updatedAt.toString(),
          };
        },
      );

      setConfigurations(configData);

      if (response.categories) {
        setCategories(response.categories);
      }

      if (response.pagination) {
        setTotalPages(response.pagination.totalPages || 1);
        setTotalCount(
          response.pagination.total || response.pagination.totalCount || 0,
        );
      }
    } catch (error) {
      console.error("Error fetching configurations:", error);
      toastError(t(messages.There_is_an_error_please_Contact_Administrator()));
    } finally {
      setLoading(false);
    }
  }, [page, limit, selectedCategory, debouncedSearchTerm, authHeaders, t]);

  useEffect(() => {
    fetchConfigurations();
  }, [page, limit, selectedCategory, debouncedSearchTerm]);

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number,
  ) => {
    setPage(value);
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setPage(1);
  };

  const handleCategoryChange = (value: string) => {
    setSelectedCategory(value);
    setPage(1);
  };

  const handleEdit = (config: Configuration) => {
    if (config.key === BROWSE_CATEGORY_MERGE_GROUPS_KEY) {
      setMergeGroupsConfig(config);
      return;
    }

    setEditingKey(config.key);
    setEditValue(config.value);
  };

  const handleCancel = () => {
    setEditingKey(null);
    setEditValue("");
  };

  const handleSave = async (config: Configuration) => {
    const parsedValue = parseValue(editValue, config.metadata.type);

    if (config.metadata.requiresRestart) {
      setPendingUpdate({ key: config.key, value: parsedValue });
      setRestartWarningOpen(true);
      return;
    }

    await updateConfiguration(config.key, parsedValue);
  };

  const updateConfiguration = async (key: string, value: any) => {
    try {
      const updateDto: UpdateConfigurationDto = {
        value: value.toString(),
      };
      await adminApiService.gfMiscellaneousAdminControllerUpdateConfiguration(
        updateDto,
        key,
        authHeaders,
      );

      toastSuccess(t(messages.Successfully_Updated()));
      setEditingKey(null);
      setEditValue("");
      await fetchConfigurations();
    } catch (error) {
      console.error("Error updating configuration:", error);
      toastError(t(messages.There_is_an_error_please_Contact_Administrator()));
    }
  };

  const handleRestartWarningConfirm = async () => {
    if (pendingUpdate) {
      await updateConfiguration(pendingUpdate.key, pendingUpdate.value);
      setRestartWarningOpen(false);
      setPendingUpdate(null);
    }
  };

  const handleRestartWarningCancel = () => {
    setRestartWarningOpen(false);
    setPendingUpdate(null);
    setEditingKey(null);
    setEditValue("");
  };

  const handleReset = async (config: Configuration) => {
    try {
      if (config.value === String(config.metadata.defaultValue)) {
        return;
      }

      await adminApiService.gfMiscellaneousAdminControllerResetConfiguration(
        config.key,
        authHeaders,
      );

      toastSuccess(t(messages.Successfully_Updated()));
      await fetchConfigurations();
    } catch (error) {
      console.error("Error resetting configuration:", error);
      toastError(t(messages.There_is_an_error_please_Contact_Administrator()));
    }
  };

  const parseValue = (value: string, type: string): any => {
    switch (type) {
      case "number":
        return Number(value);
      case "boolean":
        return value === "true";
      default:
        return value;
    }
  };

  const groupedConfigurations = configurations.reduce(
    (acc, config) => {
      const category = config.metadata.category;
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(config);
      return acc;
    },
    {} as Record<string, Configuration[]>,
  );

  return (
    <Box sx={{ mt: "65px", minHeight: "100vh", bgcolor: "#fafafa" }}>
      <AccountCardWrapper title={t(messages.Configuration_Management())}>
        <Box sx={{ p: 2.5 }}>
          <Box sx={{ mb: 2.5 }}>
            <Typography
              variant="h5"
              sx={{ fontWeight: 600, mb: 0.5, color: "#1a1a1a" }}
            >
              Configuration Management
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "#6b7280", mb: 2, fontSize: "13px" }}
            >
              Manage your system configurations with real-time updates
            </Typography>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={4}>
                <Box
                  sx={{
                    p: 1.5,
                    bgcolor: "white",
                    borderRadius: "10px",
                    border: "1px solid #e5e7eb",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ color: "#6b7280", fontWeight: 500, fontSize: "11px" }}
                  >
                    Total Configurations
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 700, color: "#f58220", mt: 0.25 }}
                  >
                    {totalCount}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box
                  sx={{
                    p: 1.5,
                    bgcolor: "white",
                    borderRadius: "10px",
                    border: "1px solid #e5e7eb",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ color: "#6b7280", fontWeight: 500, fontSize: "11px" }}
                  >
                    Current Page
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 700, color: "#1a1a1a", mt: 0.25 }}
                  >
                    {page} / {totalPages}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box
                  sx={{
                    p: 1.5,
                    bgcolor: "white",
                    borderRadius: "10px",
                    border: "1px solid #e5e7eb",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ color: "#6b7280", fontWeight: 500, fontSize: "11px" }}
                  >
                    Showing Items
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 700, color: "#1a1a1a", mt: 0.25 }}
                  >
                    {configurations.length}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Box
            sx={{
              mb: 2.5,
              p: 2,
              bgcolor: "white",
              borderRadius: "12px",
              border: "1px solid #e5e7eb",
              boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            }}
          >
            <Grid container spacing={1.5} alignItems="center">
              <Grid item xs={12} md={5}>
                <TextField
                  fullWidth
                  label="Search configurations"
                  variant="outlined"
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  placeholder="Search by key, label, or description..."
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                      bgcolor: "#fafafa",
                      "&.Mui-focused fieldset": {
                        borderColor: "#f58220",
                        borderWidth: "2px",
                      },
                      "&:hover fieldset": {
                        borderColor: "#f58220",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#f58220",
                      fontWeight: 500,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                      bgcolor: "#fafafa",
                      "&.Mui-focused fieldset": {
                        borderColor: "#f58220",
                        borderWidth: "2px",
                      },
                      "&:hover fieldset": {
                        borderColor: "#f58220",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#f58220",
                      fontWeight: 500,
                    },
                  }}
                >
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={selectedCategory}
                    label="Category"
                    onChange={(e) => handleCategoryChange(e.target.value)}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          borderRadius: "12px",
                          mt: 1,
                          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                          "& .MuiMenuItem-root": {
                            borderRadius: "8px",
                            mx: 1,
                            my: 0.5,
                            "&.Mui-selected": {
                              backgroundColor: "rgba(245, 130, 32, 0.12)",
                              fontWeight: 600,
                              "&:hover": {
                                backgroundColor: "rgba(245, 130, 32, 0.2)",
                              },
                            },
                            "&:hover": {
                              backgroundColor: "rgba(245, 130, 32, 0.08)",
                            },
                          },
                        },
                      },
                    }}
                  >
                    <MenuItem value="all">All Categories</MenuItem>
                    {categories.map((category) => (
                      <MenuItem key={category} value={category}>
                        {category}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={() => fetchConfigurations()}
                  disabled={loading}
                  sx={{
                    height: "48px",
                    borderRadius: "10px",
                    borderWidth: "2px",
                    borderColor: "#f58220",
                    color: "#f58220",
                    fontWeight: 600,
                    textTransform: "none",
                    fontSize: "15px",
                    "&:hover": {
                      borderWidth: "2px",
                      borderColor: "#d67320",
                      backgroundColor: "rgba(245, 130, 32, 0.08)",
                    },
                    "&:disabled": {
                      borderColor: "#e5e7eb",
                      color: "#9ca3af",
                    },
                  }}
                >
                  Refresh
                </Button>
              </Grid>
            </Grid>
          </Box>

          {loading && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                p: 5,
                bgcolor: "white",
                borderRadius: "12px",
                border: "1px solid #e5e7eb",
              }}
            >
              <CircularProgress size={40} sx={{ color: "#f58220", mb: 1.5 }} />
              <Typography
                variant="body1"
                sx={{ color: "#6b7280", fontWeight: 500 }}
              >
                Loading configurations...
              </Typography>
            </Box>
          )}

          {!loading && Object.keys(groupedConfigurations).length === 0 && (
            <Box
              sx={{
                textAlign: "center",
                p: 5,
                bgcolor: "white",
                borderRadius: "12px",
                border: "1px solid #e5e7eb",
              }}
            >
              <Box
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: "50%",
                  bgcolor: "rgba(245, 130, 32, 0.1)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto",
                  mb: 1.5,
                }}
              >
                <WarningIcon sx={{ fontSize: 32, color: "#f58220" }} />
              </Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, mb: 0.5, fontSize: "16px" }}
              >
                No configurations found
              </Typography>
              <Typography variant="body2" sx={{ color: "#6b7280" }}>
                Try adjusting your search or filter criteria
              </Typography>
            </Box>
          )}

          {!loading &&
            Object.entries(groupedConfigurations).map(([category, configs]) => (
              <Box key={category} sx={{ mb: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    mb: 1.5,
                    pb: 1,
                    borderBottom: "2px solid #e5e7eb",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, color: "#1a1a1a", fontSize: "16px" }}
                  >
                    {category}
                  </Typography>
                </Box>
                <Grid container spacing={2.5}>
                  {configs.map((config) => (
                    <Grid item xs={12} key={config.key}>
                      <Card
                        elevation={0}
                        sx={{
                          borderRadius: "12px",
                          border: "1px solid #e5e7eb",
                          overflow: "hidden",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                            transform: "translateY(-2px)",
                            borderColor: "#f58220",
                          },
                        }}
                      >
                        <CardContent sx={{ p: 2 }}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                              gap: 2,
                            }}
                          >
                            <Box sx={{ flex: 1 }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  mb: 1,
                                  flexWrap: "wrap",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                  }}
                                >
                                  <Typography
                                    variant="h6"
                                    sx={{
                                      fontWeight: 700,
                                      color: "#1a1a1a",
                                      fontSize: "16px",
                                    }}
                                  >
                                    {config.metadata.label}
                                  </Typography>
                                  {config.metadata.tooltip && (
                                    <Tooltip
                                      title={config.metadata.tooltip}
                                      placement="top"
                                      arrow
                                    >
                                      <InfoOutlinedIcon
                                        sx={{
                                          fontSize: "18px",
                                          color: "#9ca3af",
                                          cursor: "help",
                                          "&:hover": {
                                            color: "#f58220",
                                          },
                                        }}
                                      />
                                    </Tooltip>
                                  )}
                                </Box>
                                {config.metadata.requiresRestart ? (
                                  <Tooltip
                                    title="Changes to this configuration require a server restart to take effect"
                                    placement="top"
                                    arrow
                                  >
                                    <Chip
                                      icon={
                                        <WarningIcon
                                          sx={{ fontSize: "16px !important" }}
                                        />
                                      }
                                      label="Restart Required"
                                      size="small"
                                      sx={{
                                        bgcolor: "#fff3cd",
                                        color: "#856404",
                                        fontWeight: 600,
                                        border: "1px solid #ffeaa7",
                                        cursor: "help",
                                        "& .MuiChip-icon": {
                                          color: "#856404",
                                        },
                                      }}
                                    />
                                  </Tooltip>
                                ) : (
                                  <Tooltip
                                    title="Changes to this configuration take effect immediately without server restart"
                                    placement="top"
                                    arrow
                                  >
                                    <Chip
                                      label="Hot-Reloadable"
                                      size="small"
                                      sx={{
                                        bgcolor: "#d1f2eb",
                                        color: "#0c5460",
                                        fontWeight: 600,
                                        border: "1px solid #bee5eb",
                                        cursor: "help",
                                      }}
                                    />
                                  </Tooltip>
                                )}
                              </Box>

                              <Box
                                sx={{
                                  display: "inline-block",
                                  px: 1,
                                  py: 0.25,
                                  bgcolor: "#f3f4f6",
                                  borderRadius: "6px",
                                  mb: 1,
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontFamily: "monospace",
                                    fontSize: "12px",
                                    color: "#4b5563",
                                    fontWeight: 500,
                                  }}
                                >
                                  {config.key}
                                </Typography>
                              </Box>

                              <Typography
                                variant="body2"
                                sx={{
                                  mb: 1.5,
                                  color: "#6b7280",
                                  lineHeight: 1.5,
                                  fontSize: "13px",
                                }}
                              >
                                {config.metadata.description}
                              </Typography>

                              {editingKey === config.key ? (
                                <Box sx={{ display: "flex", gap: 1.5, mb: 2 }}>
                                  {config.metadata.type === "select" ? (
                                    <FormControl size="small" fullWidth>
                                      <Select
                                        value={editValue}
                                        onChange={(e) =>
                                          setEditValue(e.target.value)
                                        }
                                        sx={{
                                          borderRadius: "8px",
                                          "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                            {
                                              borderColor: "#f58220",
                                              borderWidth: "2px",
                                            },
                                        }}
                                      >
                                        {(config.metadata.options || []).map(
                                          (option) => (
                                            <MenuItem
                                              key={option}
                                              value={option}
                                            >
                                              {option}
                                            </MenuItem>
                                          ),
                                        )}
                                      </Select>
                                    </FormControl>
                                  ) : (
                                    <TextField
                                      size="small"
                                      value={editValue}
                                      onChange={(e) => {
                                        const value = e.target.value;
                                        if (config.metadata.type === "number") {
                                          if (
                                            value === "" ||
                                            (!value.startsWith("-") &&
                                              !isNaN(Number(value)))
                                          ) {
                                            setEditValue(value);
                                          }
                                        } else {
                                          setEditValue(value);
                                        }
                                      }}
                                      type={
                                        config.metadata.type === "number"
                                          ? "number"
                                          : "text"
                                      }
                                      inputProps={
                                        config.metadata.type === "number"
                                          ? { min: 0 }
                                          : undefined
                                      }
                                      onWheel={(e) => {
                                        if (config.metadata.type === "number") {
                                          (e.target as HTMLElement).blur();
                                        }
                                      }}
                                      onKeyDown={(e) => {
                                        if (
                                          config.metadata.type === "number" &&
                                          (e.key === "-" ||
                                            e.key === "e" ||
                                            e.key === "E")
                                        ) {
                                          e.preventDefault();
                                        }
                                      }}
                                      fullWidth
                                      sx={{
                                        "& .MuiOutlinedInput-root": {
                                          borderRadius: "8px",
                                          "&.Mui-focused fieldset": {
                                            borderColor: "#f58220",
                                            borderWidth: "2px",
                                          },
                                        },
                                      }}
                                    />
                                  )}
                                  <IconButton
                                    onClick={() => handleSave(config)}
                                    sx={{
                                      bgcolor: "#f58220",
                                      color: "white",
                                      borderRadius: "8px",
                                      "&:hover": {
                                        bgcolor: "#d67320",
                                      },
                                    }}
                                  >
                                    <SaveIcon />
                                  </IconButton>
                                  <IconButton
                                    onClick={handleCancel}
                                    sx={{
                                      bgcolor: "#f3f4f6",
                                      color: "#6b7280",
                                      borderRadius: "8px",
                                      "&:hover": {
                                        bgcolor: "#e5e7eb",
                                      },
                                    }}
                                  >
                                    <CloseIcon />
                                  </IconButton>
                                </Box>
                              ) : config.metadata.type === "json" ? (
                                <Box sx={{ mb: 1.5 }}>
                                  <Box
                                    sx={{
                                      p: 1,
                                      bgcolor: "rgba(245, 130, 32, 0.05)",
                                      borderRadius: "8px",
                                      border:
                                        "2px solid rgba(245, 130, 32, 0.2)",
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontWeight: 600,
                                        color: "#6b7280",
                                        fontSize: "13px",
                                        mb: 0.5,
                                      }}
                                    >
                                      Value (JSON):
                                    </Typography>
                                    <Box
                                      component="pre"
                                      sx={{
                                        m: 0,
                                        p: 1,
                                        bgcolor: "#fff",
                                        border: "1px solid #e5e7eb",
                                        borderRadius: "6px",
                                        fontFamily: "monospace",
                                        fontSize: "11px",
                                        color: "#1f2937",
                                        maxHeight: 220,
                                        overflow: "auto",
                                        whiteSpace: "pre",
                                      }}
                                    >
                                      {config.value}
                                    </Box>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        display: "block",
                                        mt: 1,
                                        color: "#9ca3af",
                                        fontSize: "11px",
                                      }}
                                    >
                                      {hasDedicatedEditor(config)
                                        ? "Use Edit to pick categories by name — the ids are filled in for you."
                                        : "JSON configurations have a dedicated editor. Open the matching admin page (for example, Recommendation Engine) to edit this value safely."}
                                    </Typography>
                                  </Box>
                                </Box>
                              ) : (
                                <Box sx={{ mb: 1.5 }}>
                                  <Box
                                    sx={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: 1.5,
                                      p: 1,
                                      bgcolor: "rgba(245, 130, 32, 0.05)",
                                      borderRadius: "8px",
                                      border:
                                        "2px solid rgba(245, 130, 32, 0.2)",
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontWeight: 600,
                                        color: "#6b7280",
                                        fontSize: "13px",
                                      }}
                                    >
                                      Value:
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontFamily: "monospace",
                                        fontWeight: 600,
                                        color: "#f58220",
                                      }}
                                    >
                                      {config.value}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: "#9ca3af",
                                        ml: 0.5,
                                        fontSize: "11px",
                                      }}
                                    >
                                      (Default: {config.metadata.defaultValue})
                                    </Typography>
                                  </Box>
                                </Box>
                              )}

                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 2,
                                  flexWrap: "wrap",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: "#9ca3af",
                                      fontWeight: 500,
                                    }}
                                  >
                                    Type:
                                  </Typography>
                                  <Chip
                                    label={config.metadata.type}
                                    size="small"
                                    sx={{
                                      height: "20px",
                                      fontSize: "11px",
                                      fontWeight: 600,
                                      bgcolor: "#f3f4f6",
                                      color: "#4b5563",
                                    }}
                                  />
                                </Box>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: "#9ca3af",
                                      fontWeight: 500,
                                    }}
                                  >
                                    Updated:
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: "#6b7280",
                                      fontWeight: 600,
                                    }}
                                  >
                                    {new Date(
                                      config.updatedAt,
                                    ).toLocaleDateString()}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>

                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 1,
                              }}
                            >
                              {editingKey !== config.key && (
                                <>
                                  <Tooltip
                                    title={
                                      isReadOnlyJson(config)
                                        ? "JSON configs are edited from their dedicated admin page"
                                        : "Edit value"
                                    }
                                    placement="left"
                                  >
                                    <span>
                                      <IconButton
                                        size="small"
                                        onClick={() => handleEdit(config)}
                                        disabled={isReadOnlyJson(config)}
                                        sx={{
                                          bgcolor: "rgba(245, 130, 32, 0.1)",
                                          color: "#f58220",
                                          borderRadius: "8px",
                                          "&:hover": {
                                            bgcolor: "rgba(245, 130, 32, 0.2)",
                                          },
                                          "&:disabled": {
                                            bgcolor: "#fafafa",
                                            color: "#d1d5db",
                                          },
                                        }}
                                      >
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                  <Tooltip
                                    title={
                                      isReadOnlyJson(config)
                                        ? "JSON configs are reset from their dedicated admin page"
                                        : "Reset to default"
                                    }
                                    placement="left"
                                  >
                                    <span>
                                      <IconButton
                                        size="small"
                                        onClick={() => handleReset(config)}
                                        disabled={
                                          isReadOnlyJson(config) ||
                                          config.value ===
                                            config.metadata.defaultValue
                                        }
                                        sx={{
                                          bgcolor: "#f3f4f6",
                                          color: "#6b7280",
                                          borderRadius: "8px",
                                          "&:hover": {
                                            bgcolor: "#e5e7eb",
                                          },
                                          "&:disabled": {
                                            bgcolor: "#fafafa",
                                            color: "#d1d5db",
                                          },
                                        }}
                                      >
                                        <RestoreIcon fontSize="small" />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                </>
                              )}
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            ))}

          {!loading && totalPages > 1 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mt: 3,
                mb: 1.5,
              }}
            >
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                size="medium"
                showFirstButton
                showLastButton
                sx={{
                  "& .MuiPaginationItem-root": {
                    borderRadius: "10px",
                    fontWeight: 600,
                    fontSize: "15px",
                    margin: "0 4px",
                    border: "2px solid #e5e7eb",
                    "&.Mui-selected": {
                      backgroundColor: "#f58220",
                      color: "white",
                      borderColor: "#f58220",
                      "&:hover": {
                        backgroundColor: "#d67320",
                      },
                    },
                    "&:hover": {
                      backgroundColor: "rgba(245, 130, 32, 0.08)",
                      borderColor: "#f58220",
                    },
                  },
                }}
              />
            </Box>
          )}

          {mergeGroupsConfig && (
            <BrowseCategoryMergeGroupsModal
              open
              fallbackRawValue={mergeGroupsConfig.value}
              onClose={() => setMergeGroupsConfig(null)}
              onSaved={fetchConfigurations}
            />
          )}

          <Dialog
            open={restartWarningOpen}
            onClose={handleRestartWarningCancel}
            PaperProps={{
              sx: {
                borderRadius: "16px",
                maxWidth: "500px",
                p: 1,
              },
            }}
          >
            <DialogTitle
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                pb: 2,
              }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "12px",
                  bgcolor: "#fff3cd",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <WarningIcon sx={{ color: "#856404", fontSize: 28 }} />
              </Box>
              <Box>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 700, color: "#1a1a1a" }}
                >
                  Server Restart Required
                </Typography>
                <Typography variant="caption" sx={{ color: "#6b7280" }}>
                  This change requires system restart
                </Typography>
              </Box>
            </DialogTitle>
            <DialogContent sx={{ pt: 0 }}>
              <Alert
                severity="warning"
                sx={{
                  mb: 2.5,
                  borderRadius: "12px",
                  border: "1px solid #ffeaa7",
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Important Notice
                </Typography>
                <Typography variant="body2" sx={{ fontSize: "13px" }}>
                  The change will be saved to the database, but you must restart
                  the server for the new value to take effect.
                </Typography>
              </Alert>
              {pendingUpdate && (
                <Box
                  sx={{
                    p: 2.5,
                    bgcolor: "#f9fafb",
                    borderRadius: "12px",
                    border: "1px solid #e5e7eb",
                  }}
                >
                  <Typography variant="body2" sx={{ mb: 1, color: "#6b7280" }}>
                    <strong style={{ color: "#1a1a1a" }}>Configuration:</strong>{" "}
                    {pendingUpdate.key}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#6b7280" }}>
                    <strong style={{ color: "#1a1a1a" }}>New Value:</strong>{" "}
                    <Box
                      component="span"
                      sx={{
                        fontFamily: "monospace",
                        bgcolor: "#fff",
                        px: 1,
                        py: 0.5,
                        borderRadius: "4px",
                        border: "1px solid #e5e7eb",
                      }}
                    >
                      {pendingUpdate.value.toString()}
                    </Box>
                  </Typography>
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ p: 3, pt: 0 }}>
              <Button
                onClick={handleRestartWarningCancel}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  color: "#6b7280",
                  borderRadius: "10px",
                  px: 3,
                  "&:hover": {
                    bgcolor: "#f3f4f6",
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleRestartWarningConfirm}
                variant="contained"
                startIcon={<RestartAltIcon />}
                sx={{
                  backgroundColor: "#f58220",
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: "10px",
                  px: 3,
                  boxShadow: "0 2px 8px rgba(245, 130, 32, 0.3)",
                  "&:hover": {
                    backgroundColor: "#d67320",
                    boxShadow: "0 4px 12px rgba(245, 130, 32, 0.4)",
                  },
                }}
              >
                Save & Proceed
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </AccountCardWrapper>
    </Box>
  );
};
